import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Link,
  Spinner,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useGetContractTaskWorkloadQuery, type ContractTaskWorkloadDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { FilterChip } from "../../common/components/FilterChip";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { HorizontalScrollBox, pinnedColumnEdge } from "../../common/HorizontalScrollBox";
import { ListLoadError } from "../../common/ListLoadError";
import { contractScopeText } from "./contractFigures";
import {
  columnLabel,
  countOf,
  downloadTasksPerOwner,
  kindLabel,
  sharedNames,
  sharesName,
  TASK_KINDS,
  tasksLink,
  type TaskKind,
} from "./taskWorkload";

const stickyNameColumn = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  bg: "smBackground",
  minW: "10rem",
  maxW: "16rem",
  whiteSpace: "normal",
  sx: pinnedColumnEdge("left"),
} as const;

export const TasksPerOwner = () => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const { message } = useApiError();
  const displayDate = useDisplayDate();
  const [params, setParams] = useSearchParams();
  const kind = TASK_KINDS.find((candidate) => candidate === params.get("kind")) ?? null;
  const setKind = (next: TaskKind | null) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next) updated.set("kind", next);
        else updated.delete("kind");
        return updated;
      },
      { replace: true },
    );
  const { data, isLoading, isFetching, error, refetch } = useGetContractTaskWorkloadQuery({
    orgId: authState.selectedOrg.id,
  });

  if (isLoading) return <Spinner color="smPrimary" />;
  if (error || !data) {
    return <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />;
  }

  const people = kind ? data.people.filter((person) => countOf(person, kind) > 0) : data.people;
  const shared = sharedNames(data.people);
  const scope = contractScopeText(t, data.scope);

  return (
    <Stack spacing="4">
      <Flex gap="3" wrap="wrap" align="center" justify="space-between">
        <ButtonGroup size="sm" spacing="2" flexWrap="wrap" rowGap="2">
          <FilterChip active={kind === null} label={String(t("All"))} onClick={() => setKind(null)} />
          {TASK_KINDS.map((candidate) => (
            <FilterChip
              key={candidate}
              active={kind === candidate}
              label={kindLabel(t, candidate)}
              onClick={() => setKind(candidate)}
            />
          ))}
        </ButtonGroup>
        <Button
          size="sm"
          variant="outline"
          isDisabled={people.length === 0}
          onClick={() =>
            downloadTasksPerOwner(t, people, { retrieved: displayDate(data.today), scope }, t("Tasks per owner"))
          }
        >
          {t("Download")}
        </Button>
      </Flex>
      {people.length === 0 ? (
        <Text color="smMuted">
          {kind ? t("Nobody has tasks of this kind.") : t("No contract tasks with a responsible person yet.")}
        </Text>
      ) : (
        <HorizontalScrollBox bg="smBackground" border="1px solid" borderColor="smBorder" rounded="lg">
          <Table size="sm" whiteSpace="nowrap" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <Thead>
              <Tr>
                <Th {...stickyNameColumn}>{t("Responsible")}</Th>
                {TASK_KINDS.map((column) => (
                  <Th key={column} isNumeric>
                    {columnLabel(t, column)}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {people.map((person) => (
                <Tr key={`${person.personId}-${person.external}`} data-testid={`owner-${person.personId}`}>
                  <Td {...stickyNameColumn}>
                    <Flex gap="2" align="center" wrap="wrap">
                      <Text color="smText">{person.name}</Text>
                      {!person.active && (
                        <Tag size="sm" colorScheme="gray">
                          {t("Deactivated")}
                        </Tag>
                      )}
                      {person.external && (
                        <Tag size="sm" colorScheme="gray" variant="outline">
                          {t("External")}
                        </Tag>
                      )}
                    </Flex>
                    {sharesName(shared, person) && (
                      <Text fontSize="xs" color="smMuted" overflowWrap="anywhere">
                        {person.email}
                      </Text>
                    )}
                  </Td>
                  {TASK_KINDS.map((column) => (
                    <Td key={column} isNumeric p="0">
                      <CountLink data={data} person={person} kind={column} t={t} />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </HorizontalScrollBox>
      )}
      <Box fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to: {{scope}}", { scope })}</Text>
      </Box>
    </Stack>
  );
};

const CountLink = ({
  data,
  person,
  kind,
  t,
}: {
  data: ContractTaskWorkloadDto;
  person: ContractTaskWorkloadDto["people"][number];
  kind: TaskKind;
  t: TFunction;
}) => {
  const count = countOf(person, kind);
  if (count === 0)
    return (
      <Text color="smMuted" {...countTarget}>
        0
      </Text>
    );
  return (
    <Link
      {...countTarget}
      as={RouterLink}
      to={tasksLink(person, kind, data)}
      color={kind === "overdue" ? "smError" : "smPrimary"}
      fontWeight="semibold"
      aria-label={String(t("{{count}} tasks for {{name}}: {{kind}}", { count, name: person.name, kind: kindLabel(t, kind) }))}
    >
      {count}
    </Link>
  );
};

const countTarget = { display: "block", minH: "44px", px: "4", py: "3" } as const;
